import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { type MouseEvent, useEffect, useState } from "react";
import {
  adminQueries,
  queriesForAdminView,
  type AdminDataQuery,
  type AdminView,
} from "./admin-query";
import { initials } from "./AdminUi";
import { api, ApiError } from "./api";
import { ApplicationsPanel } from "./ApplicationsPanel";
import { AuditPanel } from "./AuditPanel";
import { Brand } from "./Brand";
import { CloudflarePanel } from "./CloudflarePanel";
import { Icon, type IconName } from "./Icon";
import { OverviewPanel } from "./OverviewPanel";
import { ProviderPanel } from "./ProviderPanel";
import { UsersPanel } from "./UsersPanel";

function currentView(): AdminView {
  const segment = window.location.pathname.split("/").filter(Boolean)[1];
  return ["applications", "users", "cloudflare", "provider", "audit"].includes(
    segment,
  )
    ? (segment as AdminView)
    : "overview";
}

const warmers: Record<
  AdminDataQuery,
  (client: QueryClient) => Promise<unknown>
> = {
  users: (client) => client.query(adminQueries.users()),
  applications: (client) => client.query(adminQueries.applications()),
  provider: (client) => client.query(adminQueries.provider()),
  security: (client) => client.query(adminQueries.security()),
  securityTimeline: (client) =>
    client.infiniteQuery(adminQueries.securityInfinite()),
  cloudflare: (client) => client.query(adminQueries.cloudflare()),
};

const navItems: Array<{
  view: AdminView;
  href: string;
  icon: IconName;
  label: string;
}> = [
  { view: "overview", href: "/admin", icon: "overview", label: "Overview" },
  {
    view: "applications",
    href: "/admin/applications",
    icon: "applications",
    label: "Applications",
  },
  { view: "users", href: "/admin/users", icon: "users", label: "Users" },
  {
    view: "cloudflare",
    href: "/admin/cloudflare",
    icon: "cloudflare",
    label: "Cloudflare",
  },
  {
    view: "provider",
    href: "/admin/provider",
    icon: "security",
    label: "Provider & security",
  },
  {
    view: "audit",
    href: "/admin/audit",
    icon: "audit",
    label: "Security events",
  },
];

export function AdminPage() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<AdminView>(currentView);
  const session = useQuery(adminQueries.session());

  useEffect(() => {
    const onPopState = () => setView(currentView());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (session.isPending || !window.matchMedia("(max-width: 1100px)").matches)
      return;
    const frame = window.requestAnimationFrame(() => {
      document
        .querySelector(".nav-item--active")
        ?.scrollIntoView({ block: "nearest", inline: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [session.isPending, view]);

  function navigate(event: MouseEvent<HTMLAnchorElement>, next: AdminView) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    const item = navItems.find((candidate) => candidate.view === next);
    if (!item || next === view) return;
    window.history.pushState(null, "", item.href);
    setView(next);
  }

  function warmView(next: AdminView) {
    for (const query of queriesForAdminView(next)) {
      void warmers[query](queryClient).catch(() => undefined);
    }
  }

  async function logout() {
    try {
      const result = await api<{ logoutUrl: string }>("/api/admin/logout", {
        method: "POST",
      });
      window.location.assign(result.logoutUrl);
    } catch {
      window.location.reload();
    }
  }

  if (session.isPending) {
    return (
      <main className="center-state">
        <span className="spinner spinner--dark" />
        <p>Loading identity control plane…</p>
      </main>
    );
  }
  if (session.isError) {
    const accessError =
      session.error instanceof ApiError && session.error.status === 401
        ? "Cloudflare Access authentication is missing or invalid."
        : "The identity control plane could not be loaded.";
    return (
      <main className="center-state">
        <Brand />
        <div className="notice notice--error" role="alert">
          <strong>Administration unavailable</strong>
          <span>{accessError}</span>
        </div>
        <button
          className="button button--primary"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </main>
    );
  }

  const { user } = session.data;
  return (
    <div className="admin-shell">
      <a className="skip-link" href="#admin-content">
        Skip to content
      </a>
      <aside className="sidebar">
        <div className="sidebar__brand">
          <Brand compact />
          <span className="sidebar__product">Identity administration</span>
        </div>
        <nav aria-label="Administration">
          {navItems.map((item) => (
            <a
              aria-current={view === item.view ? "page" : undefined}
              className={`nav-item${view === item.view ? " nav-item--active" : ""}`}
              href={item.href}
              key={item.view}
              onClick={(event) => navigate(event, item.view)}
              onFocus={() => warmView(item.view)}
              onMouseEnter={() => warmView(item.view)}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </a>
          ))}
        </nav>
        <div className="sidebar__user">
          <span className="avatar avatar--small">{initials(user)}</span>
          <span>
            <strong>{user.name}</strong>
            <small>{user.email}</small>
          </span>
          <button
            aria-label="Sign out"
            className="icon-button"
            onClick={logout}
            title="Sign out"
            type="button"
          >
            <Icon name="logout" size={17} />
          </button>
        </div>
      </aside>

      <main className="admin-main" id="admin-content" tabIndex={-1}>
        {view === "overview" && <OverviewPanel />}
        {view === "applications" && <ApplicationsPanel />}
        {view === "users" && <UsersPanel />}
        {view === "cloudflare" && <CloudflarePanel />}
        {view === "provider" && <ProviderPanel />}
        {view === "audit" && <AuditPanel />}
      </main>
    </div>
  );
}
